import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Search, X, RefreshCw, Upload, FileSpreadsheet, Database, Users, Smartphone, CheckCircle, PhoneCall, Trash2, MapPin } from 'lucide-react';
import * as XLSX from 'xlsx';
import { gstinApi, gstinVisitsApi, userMappingApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast/Toast';

// Call dispositions for the followup form (per the calling campaign).
const FOLLOWUP_TYPES = ['Interested', 'Not Interested', 'No Tally', 'Accountant', 'Wrong No', 'Business Closed'];

// ── date helpers ──
const toInputDate = (s?: string | null): string => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return String(s).slice(0, 10);
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const displayDate = (s?: string | null): string => {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return String(s).slice(0, 10);
  return d.toLocaleDateString('en-GB'); // dd/mm/yyyy
};

// Normalise a sheet header so we can map loose column names on import.
// e.g. "$LedgerPinCode" -> "ledgerpincode", "$LedStateName" -> "ledstatename".
const norm = (s: any) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Column detection by SUBSTRING, so prefixed headers ($LedgerGSTIN, LedStateName,
// etc.) still map. Fields are checked in this order and each header is claimed by
// the first field it matches — so `$LedStateName` goes to STATE (checked before
// name) even though it also contains "name". Short/ambiguous tokens (pin, gst,
// zip, uin) match only as an EXACT header to avoid false hits like "shipping".
const FIELD_TOKENS: { field: 'gstin_no' | 'pincode' | 'state' | 'address' | 'customer_name'; tokens: string[] }[] = [
  { field: 'gstin_no',      tokens: ['gstinnumber', 'gstin', 'gstno', 'gstnumber', 'gst', 'uin'] },
  { field: 'pincode',       tokens: ['pincode', 'postalcode', 'postal', 'zipcode', 'pin', 'zip'] },
  { field: 'state',         tokens: ['statename', 'state'] },
  { field: 'address',       tokens: ['fulladdress', 'address', 'addr'] },
  { field: 'customer_name', tokens: ['customername', 'tradename', 'legalname', 'partyname', 'firmname', 'ledgername', 'name', 'customer', 'party'] },
];
const headerMatches = (nh: string, tokens: string[]) => tokens.some(t => nh === t || (t.length >= 4 && nh.includes(t)));

type Row = {
  id: number; customer_name: string; address: string; pincode: string; state: string; gstin_no: string;
  person_name: string; mobile_no: string; email_id: string; tally_serial_no: string; expiry_date: string;
  partner_name: string; user_id: string | null; picked_by_name: string | null;
  fu_next_date: string | null; fu_type: string | null; fu_remark: string | null; fu_at: string | null; fu_count: number;
};

export default function GstinReport() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canEdit, isAdmin, user, canCheckPermission } = useAuth();
  const { showSuccess, showError } = useToast();
  const canWrite = canEdit('gstin');
  const admin = isAdmin();
  const myId = user?.id;
  // A record can be worked on ONLY by whoever currently holds it — you must
  // pick it first, no exceptions (an admin can still pick/take over any record,
  // but must pick before updating details or logging a followup).
  const ownedByMe = (r: Row) => !!myId && !!r.user_id && String(r.user_id) === String(myId);
  const canAct = (r: Row) => ownedByMe(r);

  // Raising a field visit against a prospect. Gated on the gstin_visits
  // permission, and on having picked the record — the visit is assigned to
  // whoever picked it, so an unowned row has nobody to assign to.
  const canRaiseVisit = canCheckPermission('gstin_visits', 'create');
  const [visitBusy, setVisitBusy] = useState<number | null>(null);
  const createVisit = async (r: Row) => {
    if (!window.confirm(`Create a visit task for "${r.customer_name}"?\n\nIt will be assigned to you and appear under Visit \u203a GSTIN Visits.`)) return;
    setVisitBusy(r.id);
    try {
      await gstinVisitsApi.create({ gstin_id: r.id });
      showSuccess('Visit created', r.customer_name);
    } catch (e: any) {
      showError('Error', e?.message || 'Failed to create visit');
    } finally { setVisitBusy(null); }
  };

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [state, setState] = useState('');
  const [followupType, setFollowupType] = useState('');
  const [hasTally, setHasTally] = useState('');
  const [hasMobile, setHasMobile] = useState('');
  const [followed, setFollowed] = useState('');
  const [owned, setOwned] = useState('');
  // Existing-customer view: 'exclude' hides GSTINs already in the customer
  // master (the default "prospects" report); 'only' shows just those. Seeded
  // from the URL so a shared link keeps the view.
  const [existing, setExisting] = useState(searchParams.get('existing') === 'only' ? 'only' : 'exclude');
  // Pincode filter — seeded from the URL when arriving via the Pincode-wise
  // report drill-down (/gstin?pincode=xxx). Shown as a clearable chip.
  const [pincode, setPincode] = useState(searchParams.get('pincode') || '');
  const [pickBusy, setPickBusy] = useState<number | null>(null);
  const [stats, setStats] = useState<{ total: number; with_tally: number; with_mobile: number; followed: number } | null>(null);
  type Facets = { total: number; states: { name: string; count: number }[]; followup_types: Record<string, number>; has_tally: { yes: number; no: number }; has_mobile: { yes: number; no: number }; followed: { yes: number; no: number }; owned: { me: number; free: number }; existing: { existing: number; new: number } };
  const [facets, setFacets] = useState<Facets | null>(null);
  const fcount = (n?: number) => (n === undefined ? '' : ` (${n.toLocaleString('en-IN')})`);

  const [detailModal, setDetailModal] = useState<Row | null>(null);
  const [followupModal, setFollowupModal] = useState<Row | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  // ── Admin bulk-assign: select rows, pick a user, assign in one shot ──
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [cloudUsers, setCloudUsers] = useState<{ id: string; name: string }[]>([]);
  const [assignTo, setAssignTo] = useState('');
  const [assigning, setAssigning] = useState(false);
  const toggleRow = (id: number) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOnPageSelected = rows.length > 0 && rows.every(r => selected.has(r.id));
  const toggleAllOnPage = () => setSelected(s => {
    const n = new Set(s);
    if (allOnPageSelected) rows.forEach(r => n.delete(r.id));
    else rows.forEach(r => n.add(r.id));
    return n;
  });
  const doAssign = async () => {
    if (selected.size === 0 || !assignTo) return;
    setAssigning(true);
    try {
      const userId = assignTo === '__release__' ? null : assignTo;
      const res = await gstinApi.assign(Array.from(selected), userId);
      showSuccess('Assigned', res.message || `${res.updated} record(s) updated`);
      setSelected(new Set()); setAssignTo('');
      await load(); await loadStats();
    } catch (e: any) { showError('Error', e?.message || 'Failed to assign'); }
    finally { setAssigning(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await gstinApi.list({ page, limit: 50, search, state, pincode, followup_type: followupType, has_tally: hasTally, has_mobile: hasMobile, followed, owned, existing });
      setRows(res.data || []);
      setTotal(res.total || 0);
      setTotalPages(res.totalPages || 1);
    } catch (e: any) {
      showError('Error', e?.message || 'Failed to load GSTIN data');
    } finally { setLoading(false); }
  }, [page, search, state, pincode, followupType, hasTally, hasMobile, followed, owned, existing, showError]);

  const togglePick = async (r: Row) => {
    setPickBusy(r.id);
    try {
      if (r.user_id && (ownedByMe(r) || admin)) { await gstinApi.unpick(r.id); showSuccess('Released', r.customer_name); }
      else { await gstinApi.pick(r.id); showSuccess('Picked', r.customer_name); }
      await load();
    } catch (e: any) { showError('Error', e?.message || 'Failed'); }
    finally { setPickBusy(null); }
  };

  const loadStats = useCallback(async () => {
    // Narrow the chips + dropdown counts to the current New/Existing view so
    // they agree with the visible list (e.g. Total/Unpicked reflect prospects).
    try { const r = await gstinApi.stats({ existing }); setStats(r.data); } catch { /* ignore */ }
    try { const f = await gstinApi.facets({ existing }); setFacets(f.data); } catch { /* ignore */ }
  }, [existing]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);
  // Cloud-user list for the admin bulk-assign dropdown.
  useEffect(() => {
    if (!admin) return;
    (async () => {
      try {
        const r = await userMappingApi.getCloudUsers();
        if (r?.success) setCloudUsers((r.data || []).map((u: any) => ({ id: String(u.id), name: u.name || u.username || u.email || String(u.id) })));
      } catch { /* ignore */ }
    })();
  }, [admin]);
  useEffect(() => { setPage(1); }, [search, state, pincode, followupType, hasTally, hasMobile, followed, owned, existing]);

  // Switch between the prospects report (hide existing) and the existing-
  // customers report. Mirror the choice into the URL so it survives a refresh.
  const switchExisting = (v: 'exclude' | 'only') => {
    setExisting(v);
    if (v === 'only') searchParams.set('existing', 'only'); else searchParams.delete('existing');
    setSearchParams(searchParams, { replace: true });
  };

  // Clear the pincode chip: drop it from state AND the URL so a refresh stays clear.
  const clearPincode = () => {
    setPincode('');
    if (searchParams.get('pincode')) {
      searchParams.delete('pincode');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const onSearch = () => setSearch(searchInput.trim());
  const resetFilters = () => { setSearchInput(''); setSearch(''); setState(''); clearPincode(); setFollowupType(''); setHasTally(''); setHasMobile(''); setFollowed(''); setOwned(''); };

  const exportXlsx = async () => {
    try {
      // Export the whole current filtered set (up to 5k for a manageable file).
      const res = await gstinApi.list({ page: 1, limit: 200, search, state, followup_type: followupType, has_tally: hasTally, followed });
      let all = res.data || [];
      const pages = Math.min(res.totalPages || 1, 25);
      for (let p = 2; p <= pages; p++) {
        const r = await gstinApi.list({ page: p, limit: 200, search, state, followup_type: followupType, has_tally: hasTally, followed });
        all = all.concat(r.data || []);
      }
      const sheet = all.map((r: Row, i: number) => ({
        '#': i + 1, 'Customer Name': r.customer_name, 'GSTIN No': r.gstin_no, 'Address': r.address,
        'Pincode': r.pincode, 'State': r.state, 'Person': r.person_name, 'Mobile': r.mobile_no,
        'Email': r.email_id, 'Tally Serial': r.tally_serial_no, 'Expiry': displayDate(r.expiry_date),
        'Partner': r.partner_name, 'Last Followup': r.fu_type || '', 'Next Date': displayDate(r.fu_next_date),
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet), 'GSTIN');
      XLSX.writeFile(wb, `GSTIN_Database_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e: any) { showError('Export failed', e?.message || 'Could not export'); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-20">
        <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-gray-100 rounded" title="Back"><ChevronLeft size={20} /></button>
        <Database size={20} className="text-blue-600" />
        <h1 className="text-lg font-bold text-gray-800">GSTIN Database</h1>
        <span className="text-sm text-gray-500">({total.toLocaleString('en-IN')} records)</span>
        <div className="ml-auto flex items-center gap-2">
          {/* Compact stat chips — moved into the header, clickable to filter. */}
          {stats && (
            <div className="hidden lg:flex items-center gap-1.5 mr-1">
              {[
                { icon: <Users size={14} />,       label: 'Total',    value: stats.total,       color: 'text-gray-600',    ring: 'ring-gray-300',    active: !hasMobile && !hasTally && !followed, onClick: () => { setHasMobile(''); setHasTally(''); setFollowed(''); } },
                { icon: <Smartphone size={14} />,  label: 'Mobile',   value: stats.with_mobile, color: 'text-blue-600',    ring: 'ring-blue-400',    active: hasMobile === 'yes', onClick: () => setHasMobile(v => v === 'yes' ? '' : 'yes') },
                { icon: <CheckCircle size={14} />, label: 'Tally',    value: stats.with_tally,  color: 'text-emerald-600', ring: 'ring-emerald-400', active: hasTally === 'yes', onClick: () => setHasTally(v => v === 'yes' ? '' : 'yes') },
                { icon: <PhoneCall size={14} />,   label: 'Followed', value: stats.followed,    color: 'text-purple-600',  ring: 'ring-purple-400',  active: followed === 'yes', onClick: () => setFollowed(v => v === 'yes' ? '' : 'yes') },
              ].map(s => (
                <button key={s.label} onClick={s.onClick} title={`${s.label}: ${s.value.toLocaleString('en-IN')} — click to filter`}
                  className={`flex items-center gap-1 px-2 py-1 rounded-md border bg-gray-50 hover:bg-gray-100 ${s.active ? `ring-2 ${s.ring} border-transparent` : 'border-gray-200'}`}>
                  <span className={s.color}>{s.icon}</span>
                  <span className={`text-sm font-bold ${s.color}`}>{s.value.toLocaleString('en-IN')}</span>
                  <span className="text-[11px] text-gray-400">{s.label}</span>
                </button>
              ))}
            </div>
          )}
          <button onClick={() => { load(); loadStats(); }} className="p-2 hover:bg-gray-100 rounded text-gray-600" title="Refresh"><RefreshCw size={16} /></button>
          {/* Export & Import are admin-only. */}
          {admin && (
            <button onClick={exportXlsx} className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 rounded px-3 py-1.5">
              <FileSpreadsheet size={15} /> Export
            </button>
          )}
          {admin && (
            <button onClick={() => setImportOpen(true)} className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 border border-blue-300 bg-blue-50 hover:bg-blue-100 rounded px-3 py-1.5">
              <Upload size={15} /> Import
            </button>
          )}
        </div>
      </div>

      {/* New-prospects vs existing-customers toggle. Default hides GSTINs that
          are already in the customer master; the second button isolates them. */}
      <div className="px-4 pt-1 pb-2 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
          <button onClick={() => switchExisting('exclude')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors ${existing === 'exclude' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            <Users size={15} /> New Prospects{facets ? ` (${facets.existing.new.toLocaleString('en-IN')})` : ''}
          </button>
          <button onClick={() => switchExisting('only')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border-l border-gray-300 transition-colors ${existing === 'only' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            <CheckCircle size={15} /> Existing Customers{facets ? ` (${facets.existing.existing.toLocaleString('en-IN')})` : ''}
          </button>
        </div>
        {existing === 'only' && (
          <span className="text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
            Showing GSTINs already in the customer master (matched by GSTIN number).
          </span>
        )}
      </div>

      {/* Filters */}
      <div className="px-4 pb-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 flex-1 min-w-[220px]">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={searchInput} onChange={e => setSearchInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && onSearch()}
              placeholder="Search name / GSTIN / mobile / person…"
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <button onClick={onSearch} className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">Search</button>
        </div>
        <select value={state} onChange={e => setState(e.target.value)} className="pl-2.5 pr-8 py-2 text-sm border border-gray-300 rounded bg-white">
          <option value="">All States{fcount(facets?.total)}</option>
          {(facets?.states || []).map(s => <option key={s.name} value={s.name}>{s.name}{fcount(s.count)}</option>)}
        </select>
        <select value={followupType} onChange={e => setFollowupType(e.target.value)} className="pl-2.5 pr-8 py-2 text-sm border border-gray-300 rounded bg-white">
          <option value="">Any Followup</option>
          {FOLLOWUP_TYPES.map(t => <option key={t} value={t}>{t}{fcount(facets?.followup_types?.[t] ?? 0)}</option>)}
        </select>
        <select value={hasTally} onChange={e => setHasTally(e.target.value)} className="pl-2.5 pr-8 py-2 text-sm border border-gray-300 rounded bg-white">
          <option value="">Tally: Any</option>
          <option value="yes">Has Tally{fcount(facets?.has_tally?.yes)}</option>
          <option value="no">No Tally{fcount(facets?.has_tally?.no)}</option>
        </select>
        <select value={followed} onChange={e => setFollowed(e.target.value)} className="pl-2.5 pr-8 py-2 text-sm border border-gray-300 rounded bg-white">
          <option value="">Followup: Any</option>
          <option value="yes">Followed Up{fcount(facets?.followed?.yes)}</option>
          <option value="no">Never{fcount(facets?.followed?.no)}</option>
        </select>
        {admin && (
          <select value={owned} onChange={e => setOwned(e.target.value)} className="pl-2.5 pr-8 py-2 text-sm border border-gray-300 rounded bg-white max-w-[180px]">
            <option value="">Picked: Any</option>
            <option value="me">My Picks{fcount(facets?.owned?.me)}</option>
            <option value="picked">Picked (anyone)</option>
            <option value="free">Unpicked{fcount(facets?.owned?.free)}</option>
            {cloudUsers.length > 0 && <option disabled>──────────</option>}
            {cloudUsers.map(u => <option key={u.id} value={u.id}>Picked by {u.name}</option>)}
          </select>
        )}
        {/* Pincode-wise filter (also settable via the Pincode-wise report drill-down). */}
        <div className="relative">
          <input value={pincode} onChange={e => setPincode(e.target.value.replace(/\D/g, ''))}
            inputMode="numeric" placeholder="Pincode…"
            className="w-28 pl-2.5 pr-7 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 tabular-nums" />
          {pincode && (
            <button onClick={clearPincode} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X size={13} /></button>
          )}
        </div>
        <button onClick={resetFilters} className="inline-flex items-center gap-1 px-2.5 py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"><X size={14} /> Reset</button>
      </div>

      {/* Admin bulk-assign bar — shows the live selection count + user picker. */}
      {admin && selected.size > 0 && (
        <div className="mx-4 mb-2 flex flex-wrap items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
          <span className="text-sm font-semibold text-blue-800">{selected.size} selected</span>
          <span className="text-gray-300">|</span>
          <span className="text-sm text-gray-600">Assign to:</span>
          <select value={assignTo} onChange={e => setAssignTo(e.target.value)}
            className="pl-2.5 pr-8 py-1.5 text-sm border border-gray-300 rounded bg-white">
            <option value="">Select user…</option>
            {cloudUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            <option value="__release__">— Release (unassign) —</option>
          </select>
          <button onClick={doAssign} disabled={!assignTo || assigning}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 rounded">
            {assigning ? 'Assigning…' : 'Assign'}
          </button>
          <button onClick={() => setSelected(new Set())}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-100"><X size={14} /> Clear</button>
        </div>
      )}

      {/* Table */}
      <div className="px-4 pb-4">
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="w-full text-[13px] whitespace-nowrap">
            <thead className="bg-gray-50 text-gray-500 text-[11px] uppercase tracking-wide">
              <tr>
                {admin && (
                  <th className="px-3 py-2.5 text-center w-10">
                    <input type="checkbox" checked={allOnPageSelected} onChange={toggleAllOnPage}
                      title="Select all on this page" className="cursor-pointer" />
                  </th>
                )}
                <th className="px-3 py-2.5 text-left">#</th>
                <th className="px-3 py-2.5 text-left">Customer Name <span className="text-emerald-500 normal-case">(pick)</span></th>
                <th className="px-3 py-2.5 text-left">GSTIN No <span className="text-blue-500 normal-case">(details)</span></th>
                <th className="px-3 py-2.5 text-left">Address</th>
                <th className="px-3 py-2.5 text-left">Pincode <span className="text-purple-500 normal-case">(followup)</span></th>
                <th className="px-3 py-2.5 text-left">State</th>
                <th className="px-3 py-2.5 text-left">Person</th>
                <th className="px-3 py-2.5 text-left">Mobile</th>
                <th className="px-3 py-2.5 text-left">Email</th>
                <th className="px-3 py-2.5 text-left">Tally Serial</th>
                <th className="px-3 py-2.5 text-left">Expiry</th>
                <th className="px-3 py-2.5 text-left">Partner</th>
                <th className="px-3 py-2.5 text-left">Followup Type</th>
                <th className="px-3 py-2.5 text-left">Next Date</th>
                <th className="px-3 py-2.5 text-left">Picked By</th>
                <th className="px-3 py-2.5 text-center">Visit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={admin ? 16 : 15} className="px-3 py-10 text-center text-gray-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={admin ? 16 : 15} className="px-3 py-10 text-center text-gray-400">No records. {admin && 'Use Import to load your GSTIN sheet.'}</td></tr>
              ) : rows.map((r, i) => (
                <tr key={r.id} className={`hover:bg-blue-50/40 ${selected.has(r.id) ? 'bg-blue-50/70' : ''}`}>
                  {admin && (
                    <td className="px-3 py-1.5 text-center">
                      <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleRow(r.id)} className="cursor-pointer" />
                    </td>
                  )}
                  <td className="px-3 py-1.5 text-gray-400">{(page - 1) * 50 + i + 1}</td>
                  {/* Customer Name = Pick / Unpick toggle */}
                  <td className="px-3 py-1.5 max-w-[220px]">
                    <button disabled={!canWrite || pickBusy === r.id || (!!r.user_id && !ownedByMe(r) && !admin)}
                      onClick={() => togglePick(r)}
                      title={r.user_id ? (ownedByMe(r) || admin ? 'Click to release (unpick)' : `Picked by ${r.picked_by_name || 'another user'}`) : 'Click to pick / claim'}
                      className={`block max-w-full truncate text-left font-medium hover:underline disabled:no-underline disabled:cursor-default ${
                        r.user_id ? (ownedByMe(r) ? 'text-emerald-700' : 'text-amber-700') : 'text-gray-800'
                      }`}>
                      {r.customer_name}
                    </button>
                  </td>
                  {/* GSTIN No = Update Details (needs pick first) */}
                  <td className="px-3 py-1.5 font-mono text-[12px]">
                    {r.gstin_no ? (
                      <button disabled={!canAct(r)} onClick={() => setDetailModal(r)}
                        title={canAct(r) ? 'Click to update details' : 'Pick this record first'}
                        className="text-blue-700 hover:underline disabled:no-underline disabled:text-gray-400 disabled:cursor-default">
                        {r.gstin_no}
                      </button>
                    ) : (
                      <button disabled={!canAct(r)} onClick={() => setDetailModal(r)}
                        title={canAct(r) ? 'Click to update details' : 'Pick this record first'}
                        className="text-blue-700 hover:underline disabled:no-underline disabled:text-gray-400 disabled:cursor-default">—</button>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-gray-600 max-w-[200px] truncate" title={r.address}>{r.address || '—'}</td>
                  {/* Pincode = Followup (needs pick first) */}
                  <td className="px-3 py-1.5">
                    <button disabled={!canAct(r)} onClick={() => setFollowupModal(r)}
                      title={canAct(r) ? 'Click to log followup' : 'Pick this record first'}
                      className="text-purple-700 hover:underline disabled:no-underline disabled:text-gray-400 disabled:cursor-default">
                      {r.pincode || '—'}
                    </button>
                  </td>
                  <td className="px-3 py-1.5 text-gray-600">{r.state || '—'}</td>
                  <td className="px-3 py-1.5 text-gray-800">{r.person_name || '—'}</td>
                  <td className="px-3 py-1.5 text-gray-600">{r.mobile_no || '—'}</td>
                  <td className="px-3 py-1.5 text-gray-500 max-w-[180px] truncate" title={r.email_id}>{r.email_id || '—'}</td>
                  <td className="px-3 py-1.5 text-gray-800 font-mono text-[12px]">{r.tally_serial_no || '—'}</td>
                  <td className="px-3 py-1.5 text-gray-600">{displayDate(r.expiry_date)}</td>
                  <td className="px-3 py-1.5 text-gray-600 max-w-[160px] truncate" title={r.partner_name}>{r.partner_name || '—'}</td>
                  <td className="px-3 py-1.5">
                    {r.fu_type
                      ? <span title={r.fu_remark ? `Remark: ${r.fu_remark}` : 'No remark'}
                          className="inline-block px-2 py-0.5 rounded text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-200 cursor-help">{r.fu_type}</span>
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-1.5 text-gray-600">{displayDate(r.fu_next_date)}</td>
                  <td className="px-3 py-1.5">
                    {r.user_id
                      ? <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium border ${ownedByMe(r) ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{ownedByMe(r) ? 'You' : (r.picked_by_name || 'Picked')}</span>
                      : <span className="text-gray-400">Free</span>}
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    {canRaiseVisit && (
                      <button disabled={!canAct(r) || visitBusy === r.id} onClick={() => createVisit(r)}
                        title={canAct(r) ? 'Create a visit task for this party' : 'Pick this record first'}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded border border-transparent hover:border-blue-100 disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-default">
                        {visitBusy === r.id ? <RefreshCw className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-3 text-sm">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50">Prev</button>
            <span className="text-gray-600">Page {page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50">Next</button>
          </div>
        )}
      </div>

      {detailModal && <DetailModal row={detailModal} onClose={() => setDetailModal(null)} onSaved={() => { setDetailModal(null); load(); loadStats(); }} />}
      {followupModal && <FollowupModal row={followupModal} onClose={() => setFollowupModal(null)} onSaved={() => { setFollowupModal(null); load(); loadStats(); }} />}
      {importOpen && <ImportModal onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); loadStats(); }} />}
    </div>
  );
}

// ── Update Details modal ──
function DetailModal({ row, onClose, onSaved }: { row: Row; onClose: () => void; onSaved: () => void }) {
  const { showSuccess, showError } = useToast();
  const [form, setForm] = useState({
    customer_name: row.customer_name || '', person_name: row.person_name || '', mobile_no: row.mobile_no || '', email_id: row.email_id || '',
    tally_serial_no: row.tally_serial_no || '', expiry_date: toInputDate(row.expiry_date), partner_name: row.partner_name || '',
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await gstinApi.updateDetails(row.id, form); showSuccess('Saved', 'Details updated'); onSaved(); }
    catch (e: any) { showError('Error', e?.message || 'Failed to save'); } finally { setBusy(false); }
  };
  const field = (label: string, key: keyof typeof form, type = 'text') => (
    <div>
      <label className="block text-[11px] uppercase tracking-wide text-gray-400 mb-1">{label}</label>
      <input type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
    </div>
  );
  return (
    <Modal title="Update Details" subtitle={row.customer_name} onClose={onClose}>
      <div className="space-y-3">
        {field('Company Name', 'customer_name')}
        {field('Person Name', 'person_name')}
        {field('Mobile No', 'mobile_no')}
        {field('Email Id', 'email_id', 'email')}
        {field('Tally Serial No', 'tally_serial_no')}
        {field('Expiry Date', 'expiry_date', 'date')}
        {field('Partner Name', 'partner_name')}
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
        <button disabled={busy} onClick={save} className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
      </div>
    </Modal>
  );
}

// ── Followup modal (log + history) ──
function FollowupModal({ row, onClose, onSaved }: { row: Row; onClose: () => void; onSaved: () => void }) {
  const { showSuccess, showError } = useToast();
  const [form, setForm] = useState({ next_date: '', followup_type: FOLLOWUP_TYPES[0], remark: '' });
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  useEffect(() => { gstinApi.getFollowups(row.id).then(r => setHistory(r.data || [])).catch(() => {}); }, [row.id]);
  const save = async () => {
    setBusy(true);
    try { await gstinApi.addFollowup(row.id, form); showSuccess('Saved', 'Followup logged'); onSaved(); }
    catch (e: any) { showError('Error', e?.message || 'Failed to save'); } finally { setBusy(false); }
  };
  return (
    <Modal title="Followup" subtitle={row.customer_name} onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-gray-400 mb-1">Followup Type</label>
          <select value={form.followup_type} onChange={e => setForm(f => ({ ...f, followup_type: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded">
            {FOLLOWUP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-gray-400 mb-1">Next Date</label>
          <input type="date" value={form.next_date} onChange={e => setForm(f => ({ ...f, next_date: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded" />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-gray-400 mb-1">Remark</label>
          <textarea value={form.remark} onChange={e => setForm(f => ({ ...f, remark: e.target.value }))} rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded" />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-4 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
        <button disabled={busy} onClick={save} className="px-4 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50">{busy ? 'Saving…' : 'Save Followup'}</button>
      </div>
      {history.length > 0 && (
        <div className="mt-4 border-t border-gray-100 pt-3">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-2">History ({history.length})</div>
          <div className="space-y-2 max-h-44 overflow-auto">
            {history.map(h => (
              <div key={h.id} className="text-[12px] bg-gray-50 rounded px-2.5 py-1.5">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-purple-700">{h.followup_type || '—'}</span>
                  <span className="text-gray-400">next {displayDate(h.next_date)}</span>
                </div>
                {h.remark && <div className="text-gray-600 mt-0.5">{h.remark}</div>}
                <div className="text-[10px] text-gray-400 mt-0.5">{h.created_by || ''} · {displayDate(h.created_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Import modal (CSV/Excel → chunked upload) ──
function ImportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { showSuccess, showError } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState<{ inserted: number; skipped: number } | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearBusy, setClearBusy] = useState(false);

  const doClear = async () => {
    setClearBusy(true);
    try { const r = await gstinApi.clearAll(); showSuccess('Cleared', r.message || 'All GSTIN records deleted'); onDone(); }
    catch (e: any) { showError('Error', e?.message || 'Failed to clear'); }
    finally { setClearBusy(false); setConfirmClear(false); }
  };

  const mapRow = (raw: any): any => {
    // Assign each sheet column to at most one target field (first match wins).
    const claimed: Record<string, string | null> = { gstin_no: null, pincode: null, state: null, address: null, customer_name: null };
    for (const header of Object.keys(raw)) {
      const nh = norm(header);
      if (!nh) continue;
      for (const { field, tokens } of FIELD_TOKENS) {
        if (claimed[field] === null && headerMatches(nh, tokens)) { claimed[field] = header; break; }
      }
    }
    const val = (f: string) => (claimed[f] ? String(raw[claimed[f] as string] ?? '').trim() : '');
    return {
      customer_name: val('customer_name'),
      address: val('address'),
      pincode: val('pincode'),
      state: val('state'),
      gstin_no: val('gstin_no'),
    };
  };

  const handleFile = async (file: File) => {
    setBusy(true); setResult(null); setProgress('Reading file…');
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const raw: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      const mapped = raw.map(mapRow).filter(r => r.customer_name);
      if (mapped.length === 0) { showError('No rows', 'Could not find any Customer Name column. Expected headers: Customer Name, Address, Pincode, State, GstinNo.'); setBusy(false); return; }

      const CHUNK = 500;
      let inserted = 0, skipped = 0;
      for (let i = 0; i < mapped.length; i += CHUNK) {
        const chunk = mapped.slice(i, i + CHUNK);
        setProgress(`Uploading ${Math.min(i + CHUNK, mapped.length).toLocaleString('en-IN')} / ${mapped.length.toLocaleString('en-IN')}…`);
        const res = await gstinApi.importRows(chunk);
        inserted += res.inserted; skipped += res.skipped;
      }
      setResult({ inserted, skipped });
      showSuccess('Import complete', `${inserted} added, ${skipped} skipped`);
    } catch (e: any) {
      showError('Import failed', e?.message || 'Could not parse the file');
    } finally { setBusy(false); setProgress(''); }
  };

  return (
    <Modal title="Import GSTIN Database" subtitle="CSV or Excel" onClose={busy ? () => {} : onClose}>
      <p className="text-sm text-gray-600 mb-3">
        Upload a CSV/Excel with columns <strong>Customer Name, Address, Pincode, State, GstinNo</strong>.
        Header names are matched flexibly. Rows with a GSTIN already present are skipped.
      </p>
      <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }} />
      <button disabled={busy} onClick={() => fileRef.current?.click()}
        className="w-full inline-flex items-center justify-center gap-2 py-3 border-2 border-dashed border-blue-300 rounded-lg text-blue-700 hover:bg-blue-50 disabled:opacity-50">
        <Upload size={18} /> {busy ? (progress || 'Working…') : 'Choose file'}
      </button>
      {result && (
        <div className="mt-3 text-sm bg-emerald-50 border border-emerald-200 rounded px-3 py-2 text-emerald-800">
          Imported <strong>{result.inserted.toLocaleString('en-IN')}</strong>, skipped {result.skipped.toLocaleString('en-IN')} (duplicates / blank name).
        </div>
      )}

      {/* Danger zone — wipe everything for a fresh import. */}
      <div className="mt-4 border border-red-200 bg-red-50 rounded-lg p-3">
        <div className="text-[11px] uppercase tracking-wide text-red-500 font-semibold mb-1">Reset database</div>
        <p className="text-[12px] text-red-700/90 mb-2">Delete <strong>all</strong> GSTIN records and their followups so you can import a fresh sheet. This cannot be undone.</p>
        {!confirmClear ? (
          <button disabled={busy || clearBusy} onClick={() => setConfirmClear(true)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-red-700 border border-red-300 bg-white hover:bg-red-100 rounded px-3 py-1.5 disabled:opacity-50">
            <Trash2 size={15} /> Delete all records
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-red-700 font-medium">Are you sure?</span>
            <button disabled={clearBusy} onClick={doClear}
              className="text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded px-3 py-1.5 disabled:opacity-50">
              {clearBusy ? 'Deleting…' : 'Yes, delete everything'}
            </button>
            <button disabled={clearBusy} onClick={() => setConfirmClear(false)}
              className="text-sm border border-gray-300 rounded px-3 py-1.5 hover:bg-gray-50">Cancel</button>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button disabled={busy || clearBusy} onClick={result ? onDone : onClose} className="px-4 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">{result ? 'Done' : 'Close'}</button>
      </div>
    </Modal>
  );
}

function Modal({ title, subtitle, onClose, children }: { title: string; subtitle?: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-semibold text-gray-800">{title}</h3>
            {subtitle && <p className="text-[12px] text-gray-500">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
